// First-time agent welcome modal. Triggered exactly once per browser
// when a pro user with zero cases lands on any /pro/* route. The CTA
// opens the existing new-case wizard so the agent's first interaction
// is creating a real piece of work rather than staring at a wall of
// empty states.
//
// Why a modal (not just rely on each page's empty CTA):
// - Sets the conversational frame ("this is the agent workspace, here's
//   what we do"). Empty-state CTAs are page-local and don't explain the
//   product mental model.
// - Works whichever /pro/* page the user lands on (cases / dashboard /
//   inbox / leads etc.), not only Dashboard.
//
// Why localStorage gate (not server-side onboarding flag):
// - MVP. A server flag means a new DB column + endpoint + migration just
//   to remember "did we say hello yet". Per-browser cache is good
//   enough — worst case an agent sees the modal twice across devices.
//
// When to upgrade: real onboarding tour (multi-step product guide) →
// then move the gate to a server-side `user_prefs.onboarded_at` row.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthUser } from "@/lib/auth";
import { useCases } from "@/lib/agentCases";
import { useFlows } from "./FlowsProvider";
import { STORAGE_ONBOARDING } from "@/constants/storage";

const ONBOARDED_KEY = STORAGE_ONBOARDING.proOnboarded;

export function ProOnboardingGate() {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const { cases, loading } = useCases();
  const flows = useFlows();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Wait for cases to finish loading — we don't know what to show
    // until we know if the agent has any work yet.
    if (loading) return;
    if (!user || user.role !== "pro") return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(ONBOARDED_KEY) === "1") return;

    // Existing agent (has cases): silently mark onboarded so the modal
    // never pops up on a populated account. The dashboard already
    // shows them their work.
    if (cases.length > 0) {
      localStorage.setItem(ONBOARDED_KEY, "1");
      return;
    }

    setOpen(true);
  }, [user, cases, loading]);

  function dismiss() {
    localStorage.setItem(ONBOARDED_KEY, "1");
    setOpen(false);
  }

  function startFirstCase() {
    dismiss();
    flows.openNewCase();
  }

  if (!open) return null;

  const firstName = user?.name?.split(/\s+/)[0] ?? "there";

  return (
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        background: "rgba(20,18,12,0.45)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 460,
          width: "100%",
          background: "var(--ag-surface, white)",
          borderRadius: 16,
          padding: 28,
          boxShadow: "0 20px 50px rgba(20,18,12,0.25)",
          fontFamily: "var(--ag-font-ui)",
          color: "var(--ag-ink)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--ag-ink-3)",
            marginBottom: 8,
          }}
        >
          {t("agent.onboarding.kicker", { defaultValue: "Welcome to ProProp" })}
        </div>
        <h2
          style={{
            margin: 0,
            fontFamily: "var(--ag-font-serif)",
            fontWeight: 500,
            fontSize: 26,
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
            color: "var(--ag-ink)",
            marginBottom: 12,
          }}
        >
          {t("agent.onboarding.title", {
            defaultValue: "Let's set up your first case, {{name}}",
            name: firstName,
          })}
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            lineHeight: 1.55,
            color: "var(--ag-ink-2)",
            marginBottom: 20,
          }}
        >
          {t("agent.onboarding.body", {
            defaultValue:
              "ProProp organises your work by case — one case for each property you're selling or each buyer you're helping. Cases drive the dashboard, schedule, follow-ups, and reports. The fastest way to see how it all fits together is to create your first one.",
          })}
        </p>

        <ul
          style={{
            margin: "0 0 22px",
            padding: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 8,
            fontSize: 13,
            color: "var(--ag-ink-2)",
          }}
        >
          {[
            t("agent.onboarding.feat1", { defaultValue: "Track listings, viewings & offers in one place" }),
            t("agent.onboarding.feat2", { defaultValue: "Auto-generate client briefing packs from your data" }),
            t("agent.onboarding.feat3", { defaultValue: "Buyer leads from /chat & /rent flow into Leads automatically" }),
          ].map((s, i) => (
            <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
              <span style={{ color: "var(--ag-accent)", fontWeight: 700, flex: "none" }}>·</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <button
            onClick={dismiss}
            style={{
              padding: "9px 14px",
              borderRadius: 9,
              border: "1px solid var(--ag-rule-strong)",
              background: "transparent",
              color: "var(--ag-ink-2)",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "var(--ag-font-ui)",
            }}
          >
            {t("agent.onboarding.skip", { defaultValue: "Skip for now" })}
          </button>
          <button
            onClick={startFirstCase}
            style={{
              padding: "9px 16px",
              borderRadius: 9,
              border: "1px solid var(--ag-accent)",
              background: "var(--ag-accent)",
              color: "var(--ag-bg, white)",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "var(--ag-font-ui)",
            }}
          >
            {t("agent.onboarding.cta", { defaultValue: "Create your first case" })}
          </button>
        </div>
      </div>
    </div>
  );
}
